import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../api';
import { toast } from 'sonner';
import { Stethoscope, CheckCircle, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent } from '../components/ui/card';
import { useSiteSettings } from '../SiteSettingsContext';

const SignupPage = () => {
  const navigate = useNavigate();
  const { logoUrl, navIconUrl, orgName } = useSiteSettings();
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: '', email: '', phone: '', work_role: '',
    service_area: '', how_heard: '', knows_someone: '', notes: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.work_role || !form.service_area) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/prospects', form);
      setSubmitted(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 to-white">
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            {logoUrl ? (
              <img src={logoUrl} alt={orgName} className="h-10 w-auto object-contain" />
            ) : (
              <>
                <div className="w-9 h-9 bg-primary rounded-xl flex items-center justify-center">
                  <Stethoscope className="w-5 h-5 text-white" />
                </div>
                <span className="font-heading font-bold text-lg text-slate-800">{orgName || 'Guatemala Surgery'}</span>
              </>
            )}
          </Link>
          <Link to="/info" className="text-sm text-slate-500 hover:text-primary">Learn More</Link>
        </div>
      </nav>

      <div className="pt-28 pb-16 px-6 max-w-xl mx-auto">
        {submitted ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="text-2xl font-heading font-bold text-slate-800 mb-3">Thank You!</h1>
            <p className="text-slate-600 mb-6">
              We've received your interest form. Our team will review your submission and reach out soon with next steps.
            </p>
            <Button onClick={() => navigate('/')} variant="outline" className="rounded-full">
              Back to Home
            </Button>
          </div>
        ) : (
          <>
            <div className="mb-8">
              <h1 className="text-2xl font-heading font-bold text-slate-800 mb-2">First Time Sign Up</h1>
              <p className="text-slate-600 text-sm">
                Tell us a bit about yourself. Our team will review your information and send you a formal invitation to register.
              </p>
            </div>

            <Card>
              <CardContent className="pt-6">
                <form noValidate onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <label className="text-sm font-medium text-slate-700 mb-1 block">Full Name *</label>
                      <Input value={form.name} onChange={e => set('name', e.target.value)} placeholder="Dr. Jane Smith" required data-testid="name-input" />
                    </div>
                    <div className="col-span-2">
                      <label className="text-sm font-medium text-slate-700 mb-1 block">Email Address *</label>
                      <Input type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="jane@hospital.com" required data-testid="email-input" />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1 block">Phone</label>
                      <Input value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="555-123-4567" data-testid="phone-input" />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1 block">Hospital / Organization</label>
                      <Input value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="e.g., Mayo Clinic, Abbott Northwestern" />
                    </div>
                    <div className="col-span-2">
                      <label className="text-sm font-medium text-slate-700 mb-1 block">Role / Specialty *</label>
                      <Input value={form.work_role} onChange={e => set('work_role', e.target.value)} placeholder="e.g., General Surgeon, CRNA, OR Nurse" required data-testid="role-input" />
                    </div>
                    <div className="col-span-2">
                      <label className="text-sm font-medium text-slate-700 mb-1 block">Service Area *</label>
                      <Select value={form.service_area} onValueChange={v => set('service_area', v)} required>
                        <SelectTrigger data-testid="service-area-select"><SelectValue placeholder="Select your area..." /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="surgeons">Surgeons / Physicians</SelectItem>
                          <SelectItem value="anesthesia">Anesthesia / CRNA</SelectItem>
                          <SelectItem value="or_support">OR Nurses & Techs</SelectItem>
                          <SelectItem value="pacu">PACU / Recovery Nurses</SelectItem>
                          <SelectItem value="non_medical">Non-Medical Volunteer</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-2">
                      <label className="text-sm font-medium text-slate-700 mb-1 block">How did you hear about us?</label>
                      <Input value={form.how_heard} onChange={e => set('how_heard', e.target.value)} placeholder="Colleague, social media, etc." />
                    </div>
                    <div className="col-span-2">
                      <label className="text-sm font-medium text-slate-700 mb-1 block">Do you know someone on the team?</label>
                      <Input value={form.knows_someone} onChange={e => set('knows_someone', e.target.value)} placeholder="Name of person you know" />
                    </div>
                  </div>

                  <Button type="submit" className="w-full rounded-full bg-rojo hover:bg-rojo-dark" disabled={submitting} data-testid="submit-signup-btn">
                    {submitting ? 'Submitting...' : 'Submit Interest Form'}
                    {!submitting && <ArrowRight className="w-4 h-4 ml-2" />}
                  </Button>

                  <p className="text-xs text-slate-400 text-center">
                    Already registered before?{' '}
                    <Link to="/returning" className="text-primary hover:underline">Returning participant →</Link>
                  </p>
                </form>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
};

export default SignupPage;
